import React, { useEffect } from 'react';
import PanicError from 'Errors/Panic';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import Sentry from 'Integrations/Sentry';
import { useHistory } from 'react-router';

const RESET = 'reset';
const FETCH_STARTING = 'fetch-starting';
const FETCH_SUCCEEDED = 'fetch-succeeded';
const FETCH_FAILED = 'fetch-failed';
const SET_COLLECTION = 'set-collection';

const init = () => {
  return {
    isFetching: true,
    fetchError: null,
    collection: null,
  };
};

function reducer(state, action) {
  switch (action.type) {
    case RESET: {
      return init();
    }
    case FETCH_STARTING: {
      return {
        ...state,
        isFetching: true,
        fetchError: null,
      };
    }
    case FETCH_SUCCEEDED: {
      return {
        ...state,
        isFetching: false,
        collection: action.collection,
      };
    }
    case FETCH_FAILED: {
      return {
        ...state,
        isFetching: false,
        fetchError: action.fetchError,
      };
    }
    case SET_COLLECTION: {
      return {
        ...state,
        collection: action.collection,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

/**
 * useCollectionState
 *
 * Provides an interface for fetching/maintain a collection by facet rest
 */
const useCollectionState = (args = {}) => {
  const {
    queryParamName = undefined, // Give this a string if you would like that to be written to the url
    suppressError = false, // Set this if you are handling loading errors outside of this hook with the "loadingError" value
    relationship, // A facet-rest relationship to load
    link = '', // Are you sure you don't want to use relationship? Relationship will call getRelated() so included resources will be used if available
  } = args;

  const history = useHistory();

  const [state, _dispatch] = React.useReducer(reducer, null, init);
  const mountedRef = React.useRef(true);
  // DO NOT USE useMountedGuard HERE. It always changes the function which breaks support for link. It also doesn't affect fetchData properly
  const dispatch = React.useCallback((...dispatchArgs) => {
    if (!mountedRef.current) return undefined;
    return _dispatch(...dispatchArgs);
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // On dismount remove the query param if it exists
  useComponentDidMount(null, () => {
    if (queryParamName) {
      const urlSearchParams = new URLSearchParams(history.location.search);
      urlSearchParams.delete(queryParamName);
      history.replace({
        search: urlSearchParams.toString(),
      });
    }
  });

  // Internal function that will write the query param to the URL
  const updateQueryParam = React.useCallback((_linkURL) => {
    if (!queryParamName) {
      return;
    }

    const urlSearchParams = new URLSearchParams(history.location.search);
    urlSearchParams.set(queryParamName, _linkURL);
    history.replace({
      search: urlSearchParams.toString(),
    });
  }, [queryParamName, history.location.search]);

  // Internal function that just manages actually fetching data
  const fetchData = React.useCallback(async (actuallyFetch, fetchArgs = {}) => {
    if (!mountedRef.current) return;
    if (!actuallyFetch) {
      throw new PanicError('missing-link', 'Developer Configuration Error: A link is required to fetch data.');
    }

    const {
      onError,
      onSuccess,
    } = fetchArgs;

    try {
      dispatch({ type: FETCH_STARTING });

      const resp = await actuallyFetch();
      if (!mountedRef.current) return;

      dispatch({ type: FETCH_SUCCEEDED, collection: resp });

      if (typeof onSuccess === 'function') {
        await onSuccess(resp);
        if (!mountedRef.current) return;
      }

      if (queryParamName) {
        try {
          updateQueryParam(resp.link('self').url());
        } catch (e) {
          Sentry.createSentryErrorLog(e); // If we fail to update the query parameter then just silently report the error
        }
      }
    } catch (err) {
      dispatch({ type: FETCH_FAILED, fetchError: err });

      if (typeof onError === 'function') {
        await onError(err);
        if (!mountedRef.current) return;
      }

      // if you don't handle this then rethrow
      if (!suppressError) {
        throw new Error(err);
      }
    }
  }, [suppressError]);

  // Exported function that is capable of refetching a collection that is already in state
  const refreshCollection = React.useCallback((fetchArgs = {}) => {
    if (!state.collection) {
      return undefined;
    }

    return fetchData(() => state.collection.link('self').get(), fetchArgs);
  }, [fetchData, state]);

  // Exported function that can set the collection state directly
  const setCollection = React.useCallback((coll) => {
    dispatch({ type: SET_COLLECTION, collection: coll });
    updateQueryParam(coll.link('self').url());
  }, [updateQueryParam, dispatch]);

  // Exported function that will fetch and store a collection
  const fetchCollectionByLink = React.useCallback(async (_linkOrLinkURL, fetchArgs = {}) => {
    if (!_linkOrLinkURL) {
      throw new PanicError('missing-link', 'Developer Configuration Error: A link is required to fetch data.');
    }

    const _link = (typeof _linkOrLinkURL === 'string') ? fr.link({ data: { url: _linkOrLinkURL } }) : _linkOrLinkURL;

    return fetchData(() => _link.get(), fetchArgs);
  }, [fetchData]);

  React.useEffect(() => {
    if (link && (queryParamName || relationship)) throw new PanicError('invalid-useCollectionState-args', 'You cannot use useCollectionState with both a link and a queryParamName/relationship!');
    if (!link) return undefined;
    fetchCollectionByLink(link);
    return () => dispatch({ type: RESET });
  }, [link, queryParamName, relationship, fetchCollectionByLink, dispatch]);

  React.useEffect(() => {
    if (relationship && (queryParamName || link)) throw new PanicError('invalid-useCollectionState-args', 'You cannot use useCollectionState with both a relationship and a queryParamName/link!');
    if (!relationship) return undefined;
    fetchData(() => relationship.getRelated());
    return () => dispatch({ type: RESET });
  }, [link, queryParamName, relationship, fetchData, dispatch]);

  return {
    isInitializing: !state.collection && state.isFetching, // If no collection and fetching, then this is initializing
    isInitialized: !!state.collection,
    loading: state.isFetching,
    loadingError: state.fetchError,
    collection: state.collection,
    refreshCollection,
    setCollection,
    fetchCollectionByLink,
  };
};

export default useCollectionState;
